import apiClient from './client';
import type { MedicalTimeline } from './documents';

/**
 * One bar of the per-member document-type histogram, shape-matched to
 * `app.schemas.member_stats.DocumentTypeBreakdownItem` on the backend.
 */
export interface DocumentTypeBreakdownItem {
  type: string;
  count: number;
}

/**
 * Derived per-member stats payload surfaced on `FamilyMemberResponse`
 * and `SharedMemberItem`. Shape mirrors
 * `app.schemas.member_stats.MemberStats` 1-for-1 (snake_case, matching
 * the rest of this app's API conventions). Optional everywhere it
 * appears on a parent type so older callers and skeleton-loading UIs
 * continue to work.
 */
export interface MemberStats {
  document_count: number;
  /** ISO datetime of the most recent activity (latest `upload_date`,
   * falling back to `updated_at`/`created_at` when no documents). */
  last_activity_at: string | null;
  /** Top-4 document types by count desc; rest collapsed into
   * `document_type_other_count`. */
  document_type_breakdown: DocumentTypeBreakdownItem[];
  document_type_other_count: number;
  has_timeline: boolean;
  has_insights: boolean;
  pending_documents: number;
  /** Distinct grantees EXCLUDING the family owner. */
  shared_with_count: number;
}

export interface Family {
  id: number;
  name: string;
  owner_id: number;
  created_at: string;
  updated_at: string | null;
  members: FamilyMember[];
}

export interface FamilyMember {
  id: number;
  family_id: number;
  /** Set when returned from API with family context (e.g. getMemberById). */
  family_name?: string | null;
  family_owner_id?: number | null;
  name: string;
  date_of_birth: string | null;
  gender: 'male' | 'female' | 'other' | null;
  member_relationship: string | null;
  notes: string | null;
  /** Structured medical timeline (see `MedicalTimeline`). Persisted on
   *  the backend as JSONB. `null` until the user clicks "Genereaza" on
   *  the member detail page. */
  timeline?: MedicalTimeline | null;
  insights?: string | null;
  created_at: string;
  updated_at: string | null;
  /** Derived counters + indicators for the MemberCard list views.
   * Populated by the family-list endpoints, `null` when fetched
   * through code paths that don't need it. */
  stats?: MemberStats | null;
}

export interface FamilyCreate {
  name: string;
}

export interface FamilyUpdate {
  name?: string;
}

export interface FamilyMemberCreate {
  name: string;
  date_of_birth?: string;
  gender?: 'male' | 'female' | 'other';
  member_relationship?: string;
  notes?: string;
}

export interface FamilyMemberUpdate {
  name?: string;
  date_of_birth?: string;
  gender?: 'male' | 'female' | 'other';
  member_relationship?: string;
  notes?: string;
}

export const familiesApi = {
  createFamily: async (data: FamilyCreate): Promise<Family> => {
    const response = await apiClient.post('/api/families', data);
    return response.data;
  },

  getFamilies: async (): Promise<Family[]> => {
    const response = await apiClient.get('/api/families');
    return response.data;
  },

  getFamily: async (familyId: number): Promise<Family> => {
    const response = await apiClient.get(`/api/families/${familyId}`);
    return response.data;
  },

  updateFamily: async (familyId: number, data: FamilyUpdate): Promise<Family> => {
    const response = await apiClient.patch(`/api/families/${familyId}`, data);
    return response.data;
  },

  /** Delete a family and cascade-delete every member, document, OCR
   *  result and active share. Owner-only on the backend (HTTP 403
   *  otherwise). Returns 204 on success. */
  deleteFamily: async (familyId: number): Promise<void> => {
    await apiClient.delete(`/api/families/${familyId}`);
  },

  createMember: async (familyId: number, data: FamilyMemberCreate): Promise<FamilyMember> => {
    const response = await apiClient.post(`/api/families/${familyId}/members`, data);
    return response.data;
  },

  getMembers: async (familyId: number): Promise<FamilyMember[]> => {
    const response = await apiClient.get(`/api/families/${familyId}/members`);
    return response.data;
  },

  getMember: async (familyId: number, memberId: number): Promise<FamilyMember> => {
    const response = await apiClient.get(`/api/families/${familyId}/members/${memberId}`);
    return response.data;
  },

  getMemberById: async (memberId: number): Promise<FamilyMember> => {
    const response = await apiClient.get(`/api/families/members/${memberId}`);
    return response.data;
  },

  deleteMember: async (memberId: number): Promise<void> => {
    await apiClient.delete(`/api/families/members/${memberId}`);
  },

  updateMember: async (memberId: number, data: FamilyMemberUpdate): Promise<FamilyMember> => {
    const response = await apiClient.patch(`/api/families/members/${memberId}`, data);
    return response.data;
  },
};
